import { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, X, Check, Plus, Building2, User, Briefcase, AlertCircle, Loader2, Search, Lock, Zap, Crown, Sparkles } from 'lucide-react';
import { cn } from "@/lib/utils";
import useApi from '../../api';
import { ScrollArea } from "@/components/ui/scroll-area";
import { SalesforceField, SalesforceFieldSelect } from "./SalesforceFieldSelect";
import { SalesforceDefaultField } from "./SalesforceDefaultField";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useUser } from '../../user/UserContext';
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import SalesforceIcon from '../../icons/salesforce_logo.png';
import Upgrade from '../../modals/Upgrade';
import { VisitorMappingSections } from './VisitorMappingSections';
import { CrmValueMapping, CrmValueOption, findAutoMatch } from './CrmValueMapping';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CrmReadFieldsSection, CrmReadField } from './CrmReadFieldsSection';
import { crmEnabledAiTools } from '../tabs/crmTools';

interface RequiredField {
    name: string;
    label: string;
}

interface SalesforceFieldsModalProps {
    isOpen: boolean;
    closeModal: () => void;
    formFields: any[];
    onSave: (salesforceConfig: any) => void;
    existingConfig?: any;
    integrationId: string;
}

const OBJECT_ICONS: Record<string, React.ReactNode> = {
    lead: <User className="h-4 w-4" />,
    contact: <Briefcase className="h-4 w-4" />,
    account: <Building2 className="h-4 w-4" />,
};

export default function SalesforceFieldsModal({
    isOpen,
    closeModal,
    formFields,
    onSave,
    existingConfig,
    integrationId
}: SalesforceFieldsModalProps) {
    const { get } = useApi();
    const { checkLicenseType } = useUser();

    type SalesforceObjectKey = 'lead' | 'contact' | 'account';
    const OBJECTS: Array<{ key: SalesforceObjectKey; label: string; sobject: string; businessOnly?: boolean }> = useMemo(() => ([
        { key: 'lead', label: 'Lead', sobject: 'Lead' },
        { key: 'contact', label: 'Contact', sobject: 'Contact', businessOnly: true },
        { key: 'account', label: 'Account', sobject: 'Account', businessOnly: true },
    ]), []);

    const isBusinessEnabled = checkLicenseType('business');

    const [selectedObjects, setSelectedObjects] = useState<SalesforceObjectKey[]>(['lead']);
    const [selectedField, setSelectedField] = useState<{ object: SalesforceObjectKey; field: string } | null>(null);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);

    const [fieldMappingsByObject, setFieldMappingsByObject] = useState<Record<SalesforceObjectKey, Record<string, string>>>({
        lead: {},
        contact: {},
        account: {},
    });
    const [defaultFieldsByObject, setDefaultFieldsByObject] = useState<Record<SalesforceObjectKey, Record<string, string>>>({
        lead: {},
        contact: {},
        account: {},
    });
    const [valueMappingsByObject, setValueMappingsByObject] = useState<Record<SalesforceObjectKey, Record<string, Record<string, string>>>>({
        lead: {},
        contact: {},
        account: {},
    });
    const [accountMatchField, setAccountMatchField] = useState<string>("");
    const [enrichedSyncByObject, setEnrichedSyncByObject] = useState<{ lead: boolean; contact: boolean }>({
        lead: false,
        contact: false,
    });
    const [readFields, setReadFields] = useState<CrmReadField[]>([]);
    const [aiTools, setAiTools] = useState<string[]>([]);

    const [salesforceFieldsByObject, setSalesforceFieldsByObject] = useState<Record<SalesforceObjectKey, SalesforceField[]>>({
        lead: [],
        contact: [],
        account: [],
    });
    const [requiredFieldsByObject, setRequiredFieldsByObject] = useState<Record<SalesforceObjectKey, RequiredField[]>>({
        lead: [],
        contact: [],
        account: [],
    });
    const [isLoadingByObject, setIsLoadingByObject] = useState<Record<SalesforceObjectKey, boolean>>({
        lead: false,
        contact: false,
        account: false,
    });
    const [errorByObject, setErrorByObject] = useState<Record<SalesforceObjectKey, string | null>>({
        lead: null,
        contact: null,
        account: null,
    });

    useEffect(() => {
        if (isOpen) {
            // Initialize expanded sections
            setExpandedSections({});
            
            // Normalize existing config (backwards compatible defaults)
            const rawObjects = existingConfig?.objects;
            const isSalesforceObjectKey = (o: string): o is SalesforceObjectKey =>
                o === 'lead' || o === 'contact' || o === 'account';
            const initialObjects: SalesforceObjectKey[] = Array.isArray(rawObjects)
                ? rawObjects
                    .filter((o: any): o is string => typeof o === 'string')
                    .map((o) => o.trim().toLowerCase())
                    .filter(isSalesforceObjectKey)
                : [];

            const objects: SalesforceObjectKey[] = (initialObjects.length > 0 ? initialObjects : ['lead'])
                .filter((o, idx, arr) => arr.indexOf(o) === idx) as SalesforceObjectKey[];

            const gatedObjects = isBusinessEnabled ? objects : objects.filter(o => o === 'lead');
            setSelectedObjects(gatedObjects.length > 0 ? gatedObjects : ['lead']);

            const objectMappings = typeof existingConfig?.object_mappings === 'object' && existingConfig?.object_mappings
                ? existingConfig.object_mappings
                : {};

            const legacyLeadFields = (existingConfig?.fields && typeof existingConfig.fields === 'object') ? existingConfig.fields : {};
            const legacyLeadDefaults = (existingConfig?.default_fields && typeof existingConfig.default_fields === 'object') ? existingConfig.default_fields : {};

            const leadMapping = (objectMappings?.lead && typeof objectMappings.lead === 'object') ? objectMappings.lead : { fields: legacyLeadFields, default_fields: legacyLeadDefaults };
            const contactMapping = (objectMappings?.contact && typeof objectMappings.contact === 'object') ? objectMappings.contact : { fields: {}, default_fields: {} };
            const accountMapping = (objectMappings?.account && typeof objectMappings.account === 'object') ? objectMappings.account : { fields: {}, default_fields: {}, match_field: "" };

            setFieldMappingsByObject({
                lead: (leadMapping.fields && typeof leadMapping.fields === 'object') ? leadMapping.fields : {},
                contact: (contactMapping.fields && typeof contactMapping.fields === 'object') ? contactMapping.fields : {},
                account: (accountMapping.fields && typeof accountMapping.fields === 'object') ? accountMapping.fields : {},
            });
            setDefaultFieldsByObject({
                lead: (leadMapping.default_fields && typeof leadMapping.default_fields === 'object') ? leadMapping.default_fields : {},
                contact: (contactMapping.default_fields && typeof contactMapping.default_fields === 'object') ? contactMapping.default_fields : {},
                account: (accountMapping.default_fields && typeof accountMapping.default_fields === 'object') ? accountMapping.default_fields : {},
            });
            setValueMappingsByObject({
                lead: (leadMapping.value_mappings && typeof leadMapping.value_mappings === 'object') ? leadMapping.value_mappings : {},
                contact: (contactMapping.value_mappings && typeof contactMapping.value_mappings === 'object') ? contactMapping.value_mappings : {},
                account: (accountMapping.value_mappings && typeof accountMapping.value_mappings === 'object') ? accountMapping.value_mappings : {},
            });
            setAccountMatchField(typeof accountMapping.match_field === 'string' ? accountMapping.match_field : "");
            setEnrichedSyncByObject({
                lead: Boolean((leadMapping as any).enriched_sync),
                contact: Boolean((contactMapping as any).enriched_sync),
            });
            setReadFields(Array.isArray(existingConfig?.read_fields) ? existingConfig.read_fields : []);
            // Explicit saved ai_tools wins; legacy configs seed from read_fields/read_deals.
            setAiTools(
                Array.isArray(existingConfig?.ai_tools)
                    ? existingConfig.ai_tools
                    : Array.from(crmEnabledAiTools({ ...(existingConfig || {}), name: 'salesforce' }))
            );

            setSelectedField(null);

            setSalesforceFieldsByObject({ lead: [], contact: [], account: [] });
            setRequiredFieldsByObject({ lead: [], contact: [], account: [] });
            setErrorByObject({ lead: null, contact: null, account: null });
            setIsLoadingByObject({ lead: false, contact: false, account: false });

            const objectsToFetch = (gatedObjects.length > 0 ? gatedObjects : (['lead'] as SalesforceObjectKey[])) as SalesforceObjectKey[];
            objectsToFetch.forEach((obj) => {
                void fetchSalesforceFields(obj);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, integrationId]);

    const fetchSalesforceFields = async (objectKey: SalesforceObjectKey) => {
        const objectConfig = OBJECTS.find(o => o.key === objectKey);
        if (!objectConfig) return;

        setIsLoadingByObject(prev => ({ ...prev, [objectKey]: true }));
        setErrorByObject(prev => ({ ...prev, [objectKey]: null }));
        try {
            const response = await get(`/integrations/${integrationId}/contact-fields?object=${encodeURIComponent(objectConfig.sobject)}`);
            if (response.data.status === 'success') {
                setSalesforceFieldsByObject(prev => ({ ...prev, [objectKey]: response.data.fields || [] }));
                setRequiredFieldsByObject(prev => ({ ...prev, [objectKey]: response.data.required_fields || [] }));
            } else {
                setErrorByObject(prev => ({ ...prev, [objectKey]: 'Failed to load Salesforce fields' }));
            }
        } catch (err) {
            setErrorByObject(prev => ({ ...prev, [objectKey]: 'Failed to load Salesforce fields' }));
            console.error('Error fetching Salesforce fields:', err);
        } finally {
            setIsLoadingByObject(prev => ({ ...prev, [objectKey]: false }));
        }
    };

    const handleFieldSelect = (objectKey: SalesforceObjectKey, fieldName: string) => {
        setSelectedField({ object: objectKey, field: fieldName });
    };

    const CHOICE_FIELD_TYPES = ['select', 'dropdown', 'radio', 'multiselect'];

    const findFormFieldByMappingKey = (mappingKey: string) => {
        const name = mappingKey.startsWith('form.') ? mappingKey.slice('form.'.length) : mappingKey;
        return (formFields || []).find((f: any) => f?.name === name);
    };

    const getPicklistOptions = (field?: SalesforceField): CrmValueOption[] => {
        if (!field || (field.type !== 'picklist' && field.type !== 'multipicklist')) return [];
        return (field.picklistValues || [])
            .filter(pv => pv && pv.active !== false && pv.value)
            .map(pv => ({ value: pv.value, label: pv.label || pv.value }));
    };

    const supportsValueMapping = (objectKey: SalesforceObjectKey, mappingKey: string, salesforceFieldName?: string) => {
        const formField = findFormFieldByMappingKey(mappingKey);
        const salesforceField = (salesforceFieldsByObject[objectKey] || []).find(f => f.name === salesforceFieldName);
        return Boolean(
            formField &&
            CHOICE_FIELD_TYPES.includes(formField.type) &&
            Array.isArray(formField.options) && formField.options.length > 0 &&
            getPicklistOptions(salesforceField).length > 0
        );
    };

    const clearValueMapping = (objectKey: SalesforceObjectKey, fieldName: string) => {
        setValueMappingsByObject(prev => {
            if (!prev[objectKey]?.[fieldName]) return prev;
            const nextObj = { ...(prev[objectKey] || {}) };
            delete nextObj[fieldName];
            return { ...prev, [objectKey]: nextObj };
        });
    };

    const handleMappingUpdate = (objectKey: SalesforceObjectKey, fieldName: string, salesforceFieldName: string) => {
        const previousTarget = fieldMappingsByObject[objectKey]?.[fieldName];
        setFieldMappingsByObject(prev => ({
            ...prev,
            [objectKey]: {
                ...(prev[objectKey] || {}),
                [fieldName]: salesforceFieldName
            }
        }));
        // Value mappings target a specific field's picklist; drop them when the target changes.
        if (previousTarget && previousTarget !== salesforceFieldName) {
            clearValueMapping(objectKey, fieldName);
        }
        // Keep the row open when the new target supports option-level value mapping.
        if (!supportsValueMapping(objectKey, fieldName, salesforceFieldName)) {
            setSelectedField(null);
        }
    };

    const handleValueMappingChange = (objectKey: SalesforceObjectKey, fieldName: string, next: Record<string, string>) => {
        setValueMappingsByObject(prev => {
            const nextObj = { ...(prev[objectKey] || {}) };
            if (Object.keys(next).length === 0) {
                delete nextObj[fieldName];
            } else {
                nextObj[fieldName] = next;
            }
            return { ...prev, [objectKey]: nextObj };
        });
    };

    const handleClearMappingKey = (objectKey: SalesforceObjectKey, fieldName: string) => {
        setFieldMappingsByObject(prev => {
            const next = { ...(prev[objectKey] || {}) };
            delete next[fieldName];
            return { ...prev, [objectKey]: next };
        });
        clearValueMapping(objectKey, fieldName);
    };

    const handleCancel = () => {
        setSelectedField(null);
        closeModal();
    };

    const cleanDefaultFields = (defaults: Record<string, string>) => {
        const next = { ...(defaults || {}) };
        delete next[""];
        Object.keys(next).forEach((k) => {
            if (!k) delete next[k];
        });
        return next;
    };

    const hasAnyConfiguration = () => {
        const hasReadConfig = readFields.some(field => field.property && field.key) || aiTools.length > 0;
        return hasReadConfig || selectedObjects.some(obj => {
            const fields = fieldMappingsByObject[obj] || {};
            const defaults = cleanDefaultFields(defaultFieldsByObject[obj] || {});
            return Object.keys(fields).length > 0 || Object.keys(defaults).length > 0;
        });
    };

    const checkRequiredFieldsMapped = (objectKey: SalesforceObjectKey) => {
        const required = requiredFieldsByObject[objectKey] || [];
        const fields = fieldMappingsByObject[objectKey] || {};
        const defaults = cleanDefaultFields(defaultFieldsByObject[objectKey] || {});
        const mappedFields = new Set([
            ...Object.values(fields),
            ...Object.keys(defaults),
            ...(objectKey === 'account' && accountMatchField ? [accountMatchField] : []),
        ]);
        return required.every(field => mappedFields.has(field.name));
    };

    const isAccountMatchConfigured = () => {
        if (!selectedObjects.includes('account')) return true;
        return Boolean(accountMatchField);
    };

    /**
     * Build the value mappings to persist for an object. Unlike HubSpot (which
     * fuzzy-matches labels at sync time), Salesforce sync sends values as-is, so
     * auto-matched options are materialized into explicit entries here.
     */
    const buildValueMappings = (objectKey: SalesforceObjectKey) => {
        const fields = fieldMappingsByObject[objectKey] || {};
        const explicitMappings = valueMappingsByObject[objectKey] || {};
        const built: Record<string, Record<string, string>> = {};

        Object.entries(fields).forEach(([mappingKey, salesforceFieldName]) => {
            const salesforceField = (salesforceFieldsByObject[objectKey] || []).find(f => f.name === salesforceFieldName);
            const picklistOptions = getPicklistOptions(salesforceField);
            if (picklistOptions.length === 0) return;

            const formField = findFormFieldByMappingKey(mappingKey);
            const explicit = explicitMappings[mappingKey] || {};
            const entries: Record<string, string> = {};

            // Keep explicit choices (including stale option keys, which still
            // translate in-flight values captured before a rename).
            Object.entries(explicit).forEach(([option, value]) => {
                if (option && typeof value === 'string' && value.trim()) {
                    entries[option] = value;
                }
            });

            // Materialize auto-matches for current field options.
            if (formField && CHOICE_FIELD_TYPES.includes(formField.type) && Array.isArray(formField.options)) {
                formField.options.forEach((option: string) => {
                    if (entries[option]) return;
                    const match = findAutoMatch(option, picklistOptions);
                    if (match) entries[option] = match.value;
                });
            }

            if (Object.keys(entries).length > 0) {
                built[mappingKey] = entries;
            }
        });

        return built;
    };

    /**
     * Translate default values for picklist targets to API values at save time
     * (Salesforce sync sends values as-is, so labels would be rejected or stored raw).
     */
    const normalizePicklistDefaults = (objectKey: SalesforceObjectKey, defaults: Record<string, string>) => {
        const next = { ...defaults };
        Object.entries(next).forEach(([salesforceFieldName, rawValue]) => {
            if (typeof rawValue !== 'string' || !rawValue.trim()) return;
            const salesforceField = (salesforceFieldsByObject[objectKey] || []).find(f => f.name === salesforceFieldName);
            const picklistOptions = getPicklistOptions(salesforceField);
            if (picklistOptions.length === 0) return;

            const translate = (token: string) => findAutoMatch(token, picklistOptions)?.value || token;
            if (salesforceField?.type === 'multipicklist') {
                const separator = rawValue.includes(';') ? ';' : ',';
                next[salesforceFieldName] = rawValue
                    .split(separator)
                    .map(v => v.trim())
                    .filter(Boolean)
                    .map(translate)
                    .join(';');
            } else {
                next[salesforceFieldName] = translate(rawValue.trim());
            }
        });
        return next;
    };

    const handleSaveAll = () => {
        if (!hasAnyConfiguration()) {
            return;
        }

        const leadDefaults = normalizePicklistDefaults('lead', cleanDefaultFields(defaultFieldsByObject.lead || {}));
        const contactDefaults = normalizePicklistDefaults('contact', cleanDefaultFields(defaultFieldsByObject.contact || {}));
        const accountDefaults = normalizePicklistDefaults('account', cleanDefaultFields(defaultFieldsByObject.account || {}));

        const salesforceConfig = {
            objects: selectedObjects,
            object_mappings: {
                lead: {
                    fields: fieldMappingsByObject.lead || {},
                    default_fields: leadDefaults,
                    value_mappings: buildValueMappings('lead'),
                    enriched_sync: enrichedSyncByObject.lead,
                },
                ...(selectedObjects.includes('contact') ? {
                    contact: {
                        fields: fieldMappingsByObject.contact || {},
                        default_fields: contactDefaults,
                        value_mappings: buildValueMappings('contact'),
                        enriched_sync: enrichedSyncByObject.contact,
                    }
                } : {}),
                ...(selectedObjects.includes('account') ? {
                    account: {
                        fields: fieldMappingsByObject.account || {},
                        default_fields: accountDefaults,
                        match_field: accountMatchField || "",
                        value_mappings: buildValueMappings('account'),
                    }
                } : {}),
            },
            fields: fieldMappingsByObject.lead || {},
            default_fields: leadDefaults,
            read_fields: readFields.filter(field => field.property && field.key),
            ai_tools: aiTools,
            // read_deals mirrors the deals tool for one release of back-compat.
            read_deals: aiTools.includes('deals'),
        };

        onSave(salesforceConfig);
        closeModal();
    };

    const getSalesforceFieldLabel = (objectKey: SalesforceObjectKey, fieldName: string) => {
        const fields = salesforceFieldsByObject[objectKey] || [];
        const field = fields.find(f => f.name === fieldName);
        return field ? field.label : fieldName;
    };

    const isEditing = Boolean(existingConfig && (
        (existingConfig.fields && Object.keys(existingConfig.fields).length > 0) ||
        (existingConfig.default_fields && Object.keys(existingConfig.default_fields).length > 0) ||
        (existingConfig.object_mappings && Object.keys(existingConfig.object_mappings).length > 0)
    ));

    const handleDefaultFieldUpdate = (objectKey: SalesforceObjectKey, salesforceField: string, value: string) => {
        setDefaultFieldsByObject(prev => {
            const nextObj = { ...(prev[objectKey] || {}) };
            delete nextObj[""];
            nextObj[salesforceField] = value;
            return { ...prev, [objectKey]: nextObj };
        });
    };

    const handleRemoveDefaultField = (objectKey: SalesforceObjectKey, salesforceField: string) => {
        setDefaultFieldsByObject(prev => {
            const nextObj = { ...(prev[objectKey] || {}) };
            delete nextObj[salesforceField];
            return { ...prev, [objectKey]: nextObj };
        });
    };

    const handleAddDefaultField = (objectKey: SalesforceObjectKey) => {
        setDefaultFieldsByObject(prev => ({
            ...prev,
            [objectKey]: {
                ...(prev[objectKey] || {}),
                "": ""
            }
        }));
    };

    const toggleObject = (objectKey: SalesforceObjectKey, isLocked: boolean) => {
        if (isLocked) {
            setUpgradeModalVisible(true);
            return;
        }

        setSelectedObjects(prev => {
            const isSelected = prev.includes(objectKey);
            if (isSelected) {
                if (prev.length === 1) return prev;
                const next = prev.filter(o => o !== objectKey);
                if (selectedField?.object === objectKey) {
                    setSelectedField(null);
                }
                return next;
            }
            const next = [...prev, objectKey];
            void fetchSalesforceFields(objectKey);
            return next;
        });
    };

    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    const objectSelectLabel = useMemo(() => {
        const labels = OBJECTS.filter(o => selectedObjects.includes(o.key)).map(o => o.label);
        if (labels.length === 0) return 'Select objects...';
        if (labels.length === 1) return labels[0];
        return `${labels.length} objects selected`;
    }, [OBJECTS, selectedObjects]);

    const canSave = selectedObjects.every(obj => checkRequiredFieldsMapped(obj)) && isAccountMatchConfigured() && hasAnyConfiguration();

    const getMappedCount = (objectKey: SalesforceObjectKey) => {
        const fields = fieldMappingsByObject[objectKey] || {};
        return Object.keys(fields).length;
    };

    const getDefaultCount = (objectKey: SalesforceObjectKey) => {
        const defaults = cleanDefaultFields(defaultFieldsByObject[objectKey] || {});
        return Object.keys(defaults).length;
    };

    return (
        <>
        <Dialog open={isOpen} onOpenChange={closeModal}>
            <DialogContent className="max-w-3xl h-[85vh] flex flex-col overflow-hidden p-0">
                {/* Header */}
                <div className="px-6 py-5 border-b bg-muted/30">
                    <DialogHeader className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-background border shadow-sm flex items-center justify-center">
                                <img src={SalesforceIcon} alt="Salesforce" className="h-5" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl">Salesforce Field Mapping</DialogTitle>
                                <DialogDescription className="text-sm mt-0.5">
                                    Connect your form fields to Salesforce objects
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                </div>

                <Tabs defaultValue="sync" className="flex-1 flex flex-col overflow-hidden">
                <div className="px-6 pt-3 border-b">
                    <TabsList className="mb-3">
                        <TabsTrigger value="sync">Sync to Salesforce</TabsTrigger>
                        <TabsTrigger value="read">AI Tools</TabsTrigger>
                    </TabsList>
                </div>
                <ScrollArea className="flex-1">
                    <TabsContent value="sync" className="mt-0">
                    <div className="p-6 space-y-6">
                        {/* Object Selection Card */}
                        <div className="rounded-xl border bg-card">
                            <div className="p-4 border-b">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold">Salesforce Objects</h3>
                                        <p className="text-sm text-muted-foreground mt-0.5">
                                            Select which objects to sync data to
                                        </p>
                                    </div>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="min-w-[180px] justify-between">
                                                <span>{objectSelectLabel}</span>
                                                <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[280px] p-2" align="end">
                                            <div className="space-y-1">
                                                {OBJECTS.map((obj) => {
                                                    const isSelected = selectedObjects.includes(obj.key);
                                                    const isLocked = Boolean(obj.businessOnly) && !isBusinessEnabled;
                                                    return (
                                                        <div
                                                            key={obj.key}
                                                            className={cn(
                                                                "flex items-center gap-3 rounded-lg p-2.5 cursor-pointer transition-all",
                                                                isSelected && !isLocked && "bg-primary/10",
                                                                !isSelected && !isLocked && "hover:bg-accent",
                                                                isLocked && "bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/15"
                                                            )}
                                                            onClick={() => toggleObject(obj.key, isLocked)}
                                                        >
                                                            {isLocked ? (
                                                                <div className="h-4 w-4 rounded flex items-center justify-center bg-gradient-to-r from-purple-500 to-pink-500">
                                                                    <Lock className="h-2.5 w-2.5 text-white" />
                                                                </div>
                                                            ) : (
                                                                <Checkbox checked={isSelected} onCheckedChange={() => {}} />
                                                            )}
                                                            <div className="flex items-center gap-2 flex-1">
                                                                <div className={cn(
                                                                    isLocked && "text-purple-500"
                                                                )}>
                                                                    {OBJECT_ICONS[obj.key]}
                                                                </div>
                                                                <span className={cn(
                                                                    "text-sm font-medium",
                                                                    isLocked && "text-purple-500"
                                                                )}>{obj.label}</span>
                                                            </div>
                                                            {isLocked && (
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                                                                                <Crown className="h-3 w-3" />
                                                                                <span className="text-[10px] font-semibold">Business</span>
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p className="text-xs">Click to upgrade</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                            
                            {/* Selected Objects Pills */}
                            <div className="p-4 flex flex-wrap gap-2">
                                {selectedObjects.map((objKey) => {
                                    const obj = OBJECTS.find(o => o.key === objKey);
                                    const mappedCount = getMappedCount(objKey);
                                    const defaultCount = getDefaultCount(objKey);
                                    const isLoading = isLoadingByObject[objKey];
                                    const isComplete = !isLoading && checkRequiredFieldsMapped(objKey);
                                    
                                    return (
                                        <div
                                            key={objKey}
                                            className={cn(
                                                "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all",
                                                isLoading 
                                                    ? "bg-muted/50 border-border"
                                                    : isComplete 
                                                        ? "bg-emerald-500/10 border-emerald-500/30" 
                                                        : "bg-amber-500/10 border-amber-500/30"
                                            )}
                                        >
                                            <div className={cn(
                                                "h-6 w-6 rounded-full flex items-center justify-center",
                                                isLoading
                                                    ? "bg-slate-400 text-white"
                                                    : isComplete 
                                                        ? "bg-emerald-500 text-white" 
                                                        : "bg-amber-500 text-white"
                                            )}>
                                                {isLoading ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : isComplete ? (
                                                    <Check className="h-3.5 w-3.5" />
                                                ) : (
                                                    OBJECT_ICONS[objKey]
                                                )}
                                            </div>
                                            <span className="font-medium text-sm">{obj?.label}</span>
                                            {isLoading ? (
                                                <span className="text-[10px] text-muted-foreground">Loading...</span>
                                            ) : (
                                                <>
                                                    {mappedCount > 0 && (
                                                        <Badge variant="secondary" className="text-[10px]">
                                                            {mappedCount} mapped
                                                        </Badge>
                                                    )}
                                                    {defaultCount > 0 && (
                                                        <Badge variant="outline" className="text-[10px]">
                                                            {defaultCount} defaults
                                                        </Badge>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {!isBusinessEnabled && (
                                <div className="px-4 pb-4">
                                    <div className="rounded-lg bg-purple-500/10 border border-purple-500/20 p-3">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                                <Zap className="h-4 w-4 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium">
                                                    Unlock Contact & Account Sync
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    Sync to multiple Salesforce objects with Business
                                                </p>
                                            </div>
                                            <Button
                                                size="sm"
                                                onClick={() => setUpgradeModalVisible(true)}
                                                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white border-0 flex-shrink-0"
                                            >
                                                <Crown className="h-3 w-3 mr-1" />
                                                Upgrade
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Per-object mapping sections */}
                        {selectedObjects.map((objectKey) => {
                            const objectLabel = OBJECTS.find(o => o.key === objectKey)?.label || objectKey;
                            const isLoading = isLoadingByObject[objectKey];
                            const error = errorByObject[objectKey];
                            const salesforceFields = salesforceFieldsByObject[objectKey] || [];
                            const requiredFields = requiredFieldsByObject[objectKey] || [];
                            const fieldMappings = fieldMappingsByObject[objectKey] || {};
                            const defaultFieldMappings = defaultFieldsByObject[objectKey] || {};
                            const selectedFieldName = selectedField?.object === objectKey ? selectedField.field : null;

                            const defaultSectionKey = `${objectKey}-default`;

                            return (
                                <div key={objectKey} className="rounded-xl border bg-card overflow-hidden">
                                    {/* Object Header */}
                                    <div className="px-5 py-4 bg-muted/30 border-b">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "h-9 w-9 rounded-lg flex items-center justify-center",
                                                "bg-primary/10 text-primary"
                                            )}>
                                                {OBJECT_ICONS[objectKey]}
                                            </div>
                                            <div>
                                                <h2 className="font-semibold text-lg">{objectLabel}</h2>
                                                <p className="text-sm text-muted-foreground">
                                                    {getMappedCount(objectKey)} fields mapped
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="mx-5 mt-4 flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3">
                                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                            {error}
                                        </div>
                                    )}

                                    {isLoading ? (
                                        <div className="p-8 flex flex-col items-center justify-center text-muted-foreground">
                                            <Loader2 className="h-6 w-6 animate-spin mb-2" />
                                            <span className="text-sm">Loading Salesforce fields...</span>
                                        </div>
                                    ) : (
                                        <div className="divide-y">
                                            {/* Account Match Field */}
                                            {objectKey === 'account' && (
                                                <div className="p-5">
                                                    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                                                        <div className="flex items-start gap-3">
                                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                                <Search className="h-4 w-4 text-primary" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <h4 className="font-medium text-sm">Account Matching</h4>
                                                                <p className="text-xs text-muted-foreground mt-1 mb-3">
                                                                    Match accounts using the email domain (e.g., <code className="bg-muted px-1 rounded">acme.com</code>). 
                                                                    Select which Salesforce field contains the domain.
                                                                </p>
                                                                <SalesforceFieldSelect
                                                                    fields={salesforceFields}
                                                                    value={accountMatchField || undefined}
                                                                    onSelect={(v) => setAccountMatchField(v)}
                                                                    placeholder="Select match field"
                                                                    simpleDisplay
                                                                />
                                                                {!isAccountMatchConfigured() && (
                                                                    <p className="text-xs text-destructive mt-2 flex items-center gap-1">
                                                                        <AlertCircle className="h-3 w-3" />
                                                                        Select a match field to continue
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Enriched visitor updates (Contact and Lead) */}
                                            {(objectKey === 'contact' || objectKey === 'lead') && (
                                                <div className="p-5">
                                                    <div className="rounded-lg border p-4">
                                                        <div className="flex items-start gap-3">
                                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                                <Sparkles className="h-4 w-4 text-primary" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center justify-between gap-3">
                                                                    <h4 className="font-medium text-sm">
                                                                        {objectKey === 'contact'
                                                                            ? 'Update matched Contacts from enriched visitors'
                                                                            : 'Update matched Leads from enriched visitors'}
                                                                    </h4>
                                                                    <Switch
                                                                        checked={enrichedSyncByObject[objectKey]}
                                                                        onCheckedChange={(checked) =>
                                                                            setEnrichedSyncByObject(prev => ({ ...prev, [objectKey]: checked }))
                                                                        }
                                                                        disabled={objectKey === 'lead' && !isBusinessEnabled}
                                                                        aria-label={objectKey === 'contact'
                                                                            ? 'Update matched Contacts from enriched visitors'
                                                                            : 'Update matched Leads from enriched visitors'}
                                                                    />
                                                                </div>
                                                                <p className="text-xs text-muted-foreground mt-1">
                                                                    {objectKey === 'contact' ? (
                                                                        <>
                                                                            When visitor identification matches a visitor by work email and that
                                                                            email already exists as a Salesforce Contact, stamp the mapped System fields
                                                                            (Last Seen, Last Seen URL, Visitor Summary, …) onto that Contact and attach the session.
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            When visitor identification matches a visitor by work email and that
                                                                            email already exists as an unconverted Salesforce Lead, stamp the mapped System
                                                                            fields (Last Seen, Last Seen URL, Visitor Summary, …) onto that Lead and attach
                                                                            the session. If the email matches a Contact and Contact updates are also on,
                                                                            the Contact is updated instead.
                                                                        </>
                                                                    )}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground mt-2">
                                                                    <span className="font-medium text-foreground">Update-only.</span> Never creates
                                                                    Contacts or Leads, never writes form fields, and sets
                                                                    <code className="bg-muted px-1 rounded mx-1">Identification Source</code>
                                                                    to <code className="bg-muted px-1 rounded">enriched</code> so reports can weight
                                                                    provider matches as a signal rather than a source of truth.
                                                                </p>
                                                                {objectKey === 'lead' && !isBusinessEnabled && (
                                                                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                                                        <Crown className="h-3 w-3" />
                                                                        Requires the Business plan.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Required Fields Status */}
                                            {requiredFields.length > 0 && (
                                                <div className="p-5">
                                                    <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                                        <AlertCircle className="h-4 w-4 text-amber-500" />
                                                        Required Fields
                                                    </h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {requiredFields.map((field) => {
                                                            const satisfiedByMatchField = objectKey === 'account' && Boolean(accountMatchField) && field.name === accountMatchField;
                                                            const mappedFormField = Object.entries(fieldMappings)
                                                                .find(([_, sfField]) => sfField === field.name)?.[0];
                                                            const hasDefaultValue = field.name in cleanDefaultFields(defaultFieldMappings);
                                                            const isSatisfied = satisfiedByMatchField || Boolean(mappedFormField) || hasDefaultValue;

                                                            return (
                                                                <div
                                                                    key={`${objectKey}-${field.name}`}
                                                                    className={cn(
                                                                        "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                                                                        isSatisfied
                                                                            ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                                                                            : "bg-red-500/15 text-red-600 dark:text-red-400"
                                                                    )}
                                                                >
                                                                    {isSatisfied ? (
                                                                        <Check className="h-3 w-3" />
                                                                    ) : (
                                                                        <X className="h-3 w-3" />
                                                                    )}
                                                                    {field.label}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="p-5">
                                                <VisitorMappingSections
                                                    formFields={formFields}
                                                    mappings={fieldMappings}
                                                    selectedKey={selectedFieldName}
                                                    onSelectKey={(fieldName) => handleFieldSelect(objectKey, fieldName)}
                                                    onMapKey={(fieldName, salesforceFieldName) => handleMappingUpdate(objectKey, fieldName, salesforceFieldName)}
                                                    onClearKey={(fieldName) => handleClearMappingKey(objectKey, fieldName)}
                                                    getTargetLabel={(fieldName) => getSalesforceFieldLabel(objectKey, fieldName)}
                                                    targetLabel="Select Salesforce Field"
                                                    queryTargetLabel="Salesforce field"
                                                    renderTargetInput={({ mappingKey, value, onSelect }) => {
                                                        const formField = findFormFieldByMappingKey(mappingKey);
                                                        const salesforceField = salesforceFields.find(f => f.name === value);
                                                        const picklistOptions = getPicklistOptions(salesforceField);
                                                        const showValueMapping = supportsValueMapping(objectKey, mappingKey, value);
                                                        return (
                                                            <div className="space-y-3">
                                                                <SalesforceFieldSelect
                                                                    fields={salesforceFields}
                                                                    value={value}
                                                                    onSelect={onSelect}
                                                                    placeholder="Search and select..."
                                                                    simpleDisplay
                                                                />
                                                                {showValueMapping && formField && (
                                                                    <CrmValueMapping
                                                                        providerName="Salesforce"
                                                                        fieldOptions={formField.options}
                                                                        crmOptions={picklistOptions}
                                                                        mapping={(valueMappingsByObject[objectKey] || {})[mappingKey] || {}}
                                                                        onChange={(next) => handleValueMappingChange(objectKey, mappingKey, next)}
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                    }}
                                                />
                                            </div>

                                            {/* Default Fields Section */}
                                            <Collapsible
                                                open={expandedSections[defaultSectionKey] !== false}
                                                onOpenChange={() => toggleSection(defaultSectionKey)}
                                            >
                                                <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
                                                    <div className="flex items-center gap-2">
                                                        {expandedSections[defaultSectionKey] === false ? (
                                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                        ) : (
                                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                        )}
                                                        <h4 className="font-medium">Default Values</h4>
                                                        {Object.keys(cleanDefaultFields(defaultFieldMappings)).length > 0 && (
                                                            <Badge variant="secondary" className="text-[10px]">
                                                                {Object.keys(cleanDefaultFields(defaultFieldMappings)).length}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </CollapsibleTrigger>
                                                <CollapsibleContent>
                                                    <div className="px-5 pb-5 space-y-3">
                                                        <p className="text-xs text-muted-foreground">
                                                            Set fields that should always have a specific value
                                                        </p>
                                                        {Object.entries(defaultFieldMappings).map(([salesforceField, value]) => (
                                                            <SalesforceDefaultField
                                                                key={`${objectKey}-${salesforceField || 'new'}`}
                                                                salesforceField={salesforceField}
                                                                value={value}
                                                                onUpdate={(sfField, v) => handleDefaultFieldUpdate(objectKey, sfField, v)}
                                                                onRemove={() => handleRemoveDefaultField(objectKey, salesforceField)}
                                                                availableFields={salesforceFields}
                                                            />
                                                        ))}
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleAddDefaultField(objectKey)}
                                                            className="w-full border-dashed"
                                                        >
                                                            <Plus className="h-4 w-4 mr-2" />
                                                            Add Default Field
                                                        </Button>
                                                    </div>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    </TabsContent>
                    <TabsContent value="read" className="mt-0">
                        <div className="p-6">
                            <CrmReadFieldsSection
                                integrationId={integrationId}
                                provider="salesforce"
                                readFields={readFields}
                                setReadFields={setReadFields}
                                aiTools={aiTools}
                                setAiTools={setAiTools}
                                renderPropertySelect={({ fields, value, onSelect }) => (
                                    <SalesforceFieldSelect
                                        fields={fields}
                                        value={value}
                                        onSelect={onSelect}
                                        placeholder="Search and select..."
                                        simpleDisplay
                                    />
                                )}
                            />
                        </div>
                    </TabsContent>
                </ScrollArea>
                </Tabs>

                {/* Footer */}
                <div className="border-t px-6 py-4 bg-muted/30 flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        {hasAnyConfiguration() ? (
                            <span className="flex items-center gap-1.5">
                                <Check className="h-4 w-4 text-emerald-500" />
                                Ready to save
                            </span>
                        ) : (
                            <span>Map at least one field to save</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveAll}
                            disabled={!canSave}
                        >
                            {isEditing ? 'Save Changes' : 'Create Mapping'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        <Upgrade
            showModal={upgradeModalVisible}
            setShowModal={setUpgradeModalVisible}
            sessionId={null}
        />
        </>
    );
}
